/**
 * AdminErrorBoundary Component (T142 - Phase 9 Polish)
 * Error boundary for admin portal components
 * 
 * Catches React errors in component tree and displays fallback UI
 * Prevents entire admin portal from crashing due to component errors
 * Logs errors for debugging and monitoring
 */

import { Component, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Card } from '@/shared/components/ui/card';

type ErrorBoundaryProps = {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
  onReset?: () => void;
};

type ErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
};

/**
 * Error Boundary for Admin Portal
 * 
 * Usage:
 * ```tsx
 * <AdminErrorBoundary>
 *   <MyComponent />
 * </AdminErrorBoundary>
 * ```
 * 
 * With custom messages:
 * ```tsx
 * <AdminErrorBoundary 
 *   fallbackTitle="User Management Error"
 *   fallbackMessage="Failed to load user management interface"
 * >
 *   <UsersTab />
 * </AdminErrorBoundary>
 * ```
 */
export class AdminErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error details for debugging
    console.error('AdminErrorBoundary caught an error:', error);
    console.error('Error info:', errorInfo);
    
    // You can also send error to logging service here
    // Example: logErrorToService(error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });
  }

  handleReset = () => {
    // Reset error boundary state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
    
    // Call custom reset handler if provided
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  handleGoHome = () => {
    // Navigate to admin portal home
    window.location.href = '/admin';
  };

  render() {
    if (this.state.hasError) {
      const { fallbackTitle, fallbackMessage } = this.props;
      const isDevelopment = import.meta.env.MODE === 'development';

      return (
        <div className="min-h-[400px] flex items-center justify-center p-6">
          <Card className="max-w-2xl w-full p-8">
            <div className="text-center">
              {/* Error Icon */}
              <div className="flex justify-center mb-4">
                <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertTriangle className="h-8 w-8 text-red-600" />
                </div>
              </div>

              {/* Error Title */}
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {fallbackTitle || 'Something went wrong'}
              </h2>

              {/* Error Message */}
              <p className="text-gray-600 mb-6">
                {fallbackMessage || 
                  'An unexpected error occurred in the admin portal. Please try refreshing the page or contact support if the problem persists.'}
              </p>

              {/* Error Details (Development Only) */}
              {isDevelopment && this.state.error && (
                <div className="mb-6 text-left">
                  <details className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <summary className="cursor-pointer font-medium text-red-900 mb-2">
                      Error Details (Development Mode)
                    </summary>
                    <div className="space-y-2">
                      <div>
                        <p className="text-sm font-semibold text-red-900">Error:</p>
                        <p className="text-sm text-red-800 font-mono">
                          {this.state.error.toString()}
                        </p>
                      </div>
                      {this.state.errorInfo && (
                        <div>
                          <p className="text-sm font-semibold text-red-900">Stack Trace:</p>
                          <pre className="text-xs text-red-800 overflow-auto max-h-40 bg-red-100 p-2 rounded">
                            {this.state.errorInfo.componentStack}
                          </pre>
                        </div>
                      )}
                    </div>
                  </details>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={this.handleReset}
                  className="flex items-center gap-2 px-6 py-2.5 bg-[#026857] text-white rounded-md hover:bg-[#024d43] transition-colors font-medium"
                >
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </button>
                <button
                  onClick={this.handleGoHome}
                  className="flex items-center gap-2 px-6 py-2.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors font-medium"
                >
                  <Home className="h-4 w-4" />
                  Go to Dashboard
                </button>
              </div>

              {/* Support Message */}
              <p className="mt-6 text-sm text-gray-500">
                If this error persists, please contact technical support with the error details above.
              </p>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Hook-based wrapper for functional components
 * Use this when you need to add error boundary with hooks
 */
export function withAdminErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallbackTitle?: string,
  fallbackMessage?: string,
) {
  return function WithErrorBoundary(props: P) {
    return (
      <AdminErrorBoundary 
        fallbackTitle={fallbackTitle} 
        fallbackMessage={fallbackMessage}
      >
        <Component {...props} />
      </AdminErrorBoundary>
    );
  };
}
